/**
 * Generic lock-screen copy for Lot 4B prospect response Web Push.
 * No property address, city, prospect id, campaign/mailing, or contact fields.
 */

export const PROSPECT_PUSH_OFFER_TYPE = 'prospect-push-offer';
export const PROSPECT_PUSH_ACK_TYPE = 'prospect-push-ack';
export const PROSPECT_PUSH_REFRESH_TYPE = 'prospect-push-refresh';
export const PROSPECT_PUSH_CONSUMER_ACK_BUDGET_MS = 800;

export function prospectPushNotificationTitle(lang) {
  return lang === 'en' ? 'Rentalyzer Admin' : 'Admin Rentalyzer';
}

export function prospectPushNotificationBody(lang) {
  return lang === 'en'
    ? 'New prospect response available'
    : 'Nouvelle réponse de prospect disponible';
}

/**
 * HashRouter path that opens the opaque-ref resolver (never embeds campaign/mailing ids).
 * @param {string} pushRef
 */
export function buildProspectPushClickPath(pushRef) {
  const ref = typeof pushRef === 'string' ? pushRef.trim() : '';
  return `/admin/prospection/push?ref=${encodeURIComponent(ref)}`;
}

/**
 * Absolute URL for notification click. HashRouter-safe (hash holds the route).
 * @param {{ origin: string, pushRef: string }} args
 */
export function buildProspectPushClickUrl({ origin, pushRef }) {
  const base = typeof origin === 'string' ? origin.replace(/\/$/, '') : '';
  return `${base}/#${buildProspectPushClickPath(pushRef)}`;
}

/**
 * A focused same-origin window alone must NOT suppress the system notification.
 * Only an acknowledgement that the offer was successfully handled by an eligible
 * foreground admin consumer may suppress.
 *
 * @param {{ acknowledgements?: Array<{ requestId?: string, eligible?: boolean, handled?: boolean, focused?: boolean, visibilityState?: string }>, requestId?: string }} args
 */
export function shouldSuppressForegroundPushAlert({ acknowledgements = [], requestId } = {}) {
  const list = Array.isArray(acknowledgements) ? acknowledgements : [];
  return list.some(
    (ack) =>
      ack &&
      ack.eligible === true &&
      ack.handled === true &&
      typeof ack.requestId === 'string' &&
      ack.requestId === requestId &&
      (ack.focused === true || ack.focused == null) &&
      (ack.visibilityState == null || ack.visibilityState === 'visible'),
  );
}

/**
 * Pure helper: wait for eligible handled ACKs with a bounded budget, then decide.
 * Used by the service worker and unit tests with injected I/O.
 */
export async function collectForegroundPushAcknowledgements({
  requestId,
  offerToClients,
  waitForAcks,
  budgetMs = PROSPECT_PUSH_CONSUMER_ACK_BUDGET_MS,
} = {}) {
  if (typeof offerToClients === 'function') {
    await offerToClients({ requestId });
  }
  const acknowledgements =
    typeof waitForAcks === 'function' ? await waitForAcks({ requestId, budgetMs }) : [];
  const suppress = shouldSuppressForegroundPushAlert({ acknowledgements, requestId });
  return { suppress, acknowledgements };
}
